import React, { FC, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import PageContainer from "../../../components/pageContainer";
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../../../routes/paths";
import { jaJP } from "@mui/x-data-grid/locales";
import { MoreVert, CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import { AccountStatus, UserCreate, UserResponse, UserRole } from "../../../client";
import useUser from "../../../hooks/useUser";
import dayjs from "dayjs";

interface AccountCreateDialogProps {
  openRegisterDialog: boolean;
  setOpenRegisterDialog: (open: boolean) => void;
  addAccount?: (account: any) => void;
  onSuccess: (message: string) => void;
}

interface FormErrors {
  [key: string]: string | undefined;
  login_id?: string;
  name?: string;
  password?: string;
  email?: string;
  phone?: string;
  role?: string;
}

const AccountCreateDialog: FC<AccountCreateDialogProps> = ({
  openRegisterDialog,
  setOpenRegisterDialog,
  addAccount,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<UserCreate>({
    login_id: '',
    name: '',
    phone: '',
    email: '',
    role: 'admin',
    status: 'active',
    password: '',
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createUserAdmin } = useUser();

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    if (!formData.login_id.trim()) {
      errors.login_id = 'ログインIDを入力してください';
      isValid = false;
    } else if (formData.login_id.length < 4) {
      errors.login_id = 'ログインIDは4文字以上で入力してください';
      isValid = false;
    }

    if (!formData.name.trim()) {
      errors.name = '氏名を入力してください';
      isValid = false;
    }

    if (!formData.password) {
      errors.password = 'パスワードを入力してください';
      isValid = false;
    } else if (formData.password.length < 8) {
      errors.password = 'パスワードは8文字以上で入力してください';
      isValid = false;
    }

    if (formData.role === 'admin' && !formData.email) {
      errors.email = '管理者の場合、メールアドレスは必須です';
      isValid = false;
    } else if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = '有効なメールアドレスを入力してください';
      isValid = false;
    }

    if (formData.phone && !/^[0-9]+$/.test(formData.phone)) {
      errors.phone = '有効な電話番号を入力してください';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const filteredValue = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({ ...prev, [name]: filteredValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    setFormErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);
    setFormErrors({});

    try {
      const response = await createUserAdmin({ requestBody: formData });

      if (response) {
        if (addAccount) {
          addAccount({
            id: response.id,
            username: response.name,
            email: response.email,
            role: response.role,
            created_at: response.created_at,
            updated_at: response.updated_at,
          });
        }
        onSuccess("ユーザーを登録しました");
        handleClose();
      }
    } catch (err: any) {
      console.log('Submit error:', err);

      if (err.type === 'DuplicateLoginIdError') {
        setFormErrors(prev => ({
          ...prev,
          login_id: err.message || 'このログインIDは既に使用されています'
        }));
      } else if (err.type === 'DuplicateEmailError') {
        setFormErrors(prev => ({
          ...prev,
          email: err.message || 'このメールアドレスは既に使用されています'
        }));
      } else if (err.type === 'ValidationError') {
        if (err.details) {
          const newFormErrors: FormErrors = {};
          Object.entries(err.details).forEach(([field, message]) => {
            if (typeof message === 'string') {
              newFormErrors[field] = message;
            }
          });
          setFormErrors(newFormErrors);
        } else {
          setError(err.message || '入力内容を確認してください');
        }
      } else {
        setError(err.message || '予期せぬエラーが発生しました');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      login_id: '',
      name: '',
      phone: '',
      email: '',
      role: 'internal_worker',
      status: 'active',
      password: '',
    });
    setFormErrors({});
    setError(null);
    setOpenRegisterDialog(false);
  };

  return (
    <Dialog open={openRegisterDialog} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0 }}>アカウント登録</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" sx={{ color: "gray", mb: 2 }}>
          アカウント情報を入力してください
        </Typography>
        <TextField
          fullWidth
          margin="normal"
          name="name"
          label="氏名"
          value={formData.name}
          onChange={handleChange}
          required
          error={!!formErrors.name}
          helperText={formErrors.name}
        />
        <TextField
          fullWidth
          margin="dense"
          name="login_id"
          label="ログインID"
          value={formData.login_id}
          onChange={handleChange}
          required
          error={!!formErrors.login_id}
          helperText={formErrors.login_id || "※ ログインIDは4文字以上で入力してください"}
          sx={{ mt: 1 }}
        />
        <TextField
          fullWidth
          margin="dense"
          name="password"
          type="password"
          label="パスワード"
          value={formData.password}
          onChange={handleChange}
          required
          error={!!formErrors.password}
          helperText={formErrors.password || "※ パスワードは8文字以上で入力してください"}
          sx={{ mt: 1 }}
        />
        <TextField
          fullWidth
          margin="dense"
          name="email"
          label="メールアドレス"
          type="email"
          value={formData.email || ''}
          onChange={handleChange}
          error={!!formErrors.email}
          helperText={formErrors.email || (formData.role === 'admin' ? "※ 管理者の場合、メールアドレスの入力は必須です" : undefined)}
          sx={{ mt: 1, mb: 1 }}
        />
        <TextField
          fullWidth
          margin="dense"
          name="phone"
          label="電話番号"
          value={formData.phone || ''}
          onChange={handleChange}
          error={!!formErrors.phone}
          helperText={formErrors.phone}
        />
        <TextField
          fullWidth
          margin="normal"
          name="role"
          select
          label="役割"
          value={formData.role}
          onChange={handleChange}
        >
          <MenuItem value="admin">管理者</MenuItem>
          <MenuItem value="internal_worker">社内作業者</MenuItem>
          <MenuItem value="external_worker">委託先作業者</MenuItem>
        </TextField>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ display: "flex", flexDirection: "column", px: 3, gap: 1, pb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          fullWidth
          disabled={isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={24} /> : '登録'}
        </Button>
        <Button
          sx={{ color: "#777" }}
          onClick={handleClose}
          fullWidth
          disabled={isSubmitting}
        >
          キャンセル
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ActionMenuProps {
  params: GridRenderCellParams;
  isArchived: boolean;
  onDelete: (id: string) => Promise<void>;
  onRestore: (id: string) => Promise<void>;
  onError: (message: string) => void;
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  params,
  isArchived,
  onDelete,
  onRestore,
  onError
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await onDelete(params.row.id);
      setOpenDeleteDialog(false);
    } catch (error: any) {
      onError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async () => {
    setIsLoading(true);
    try {
      await onRestore(params.row.id);
      setOpenRestoreDialog(false);
    } catch (error: any) {
      onError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVert />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {!isArchived && (
          <MenuItem
            onClick={() => {
              handleClose();
              navigate(PATHS.ACCOUNT_DETAIL.replaceAll(":id", params.row.id.toString()));
            }}
          >
            編集
          </MenuItem>
        )}

        {!isArchived ? (
          <MenuItem
            onClick={() => {
              handleClose();
              setOpenDeleteDialog(true);
            }}
            sx={{ color: 'error.main' }}
          >
            削除
          </MenuItem>
        ) : (
          <>
            <MenuItem
              onClick={() => {
                handleClose();
                navigate(PATHS.ACCOUNT_DETAIL.replaceAll(":id", params.row.id.toString()));
              }}
            >
              編集
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClose();
                setOpenRestoreDialog(true);
              }}
              sx={{ color: 'success.main' }}
            >
              復元
            </MenuItem>
          </>
        )}
      </Menu>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => !isLoading && setOpenDeleteDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>
          <Typography>このアカウントを削除してもよろしいですか？</Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            削除されたアカウントは後で復元することができます。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setOpenDeleteDialog(false)} disabled={isLoading}>
            キャンセル
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : '削除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 復元確認ダイアログ */}
      <Dialog
        open={openRestoreDialog}
        onClose={() => !isLoading && setOpenRestoreDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>復元確認</DialogTitle>
        <DialogContent>
          <Typography>このアカウントを復元してもよろしいですか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setOpenRestoreDialog(false)} disabled={isLoading}>
            キャンセル
          </Button>
          <Button variant="contained" color="success"
            onClick={handleRestore} disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : '復元'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AccountList: FC = () => {
  const navigate = useNavigate();
  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
  const [data, setData] = useState<UserResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isArchivedMode, setIsArchivedMode] = useState(false);

  const { getUserList, deleteUser, restoreUser, } = useUser();

  const deleteAccount = async (id: string) => {
    try {
      const message = await deleteUser({ userId: id });
      setSuccessMessage(message || "ユーザーを削除しました");
      await fetchData();
    } catch (err: any) {
      const errorMessage = err.message || "削除に失敗しました";
      setErrorMessage(errorMessage);
      throw err;
    }
  };

  const restoreAccount = async (id: string) => {
    try {
      await restoreUser({ userId: id });
      setSuccessMessage("ユーザーを復元しました");
      await fetchData();
    } catch (err: any) {
      const errorMessage = err.message || "復元に失敗しました";
      setErrorMessage(errorMessage);
      throw err;
    }
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getUserList({
        limit: paginationModel.pageSize,
        skip: paginationModel.page * paginationModel.pageSize,
        archived: isArchivedMode,
      });

      if (response) {
        setData(response.items);
        setTotal(response.total);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getUserList, paginationModel.page, paginationModel.pageSize, isArchivedMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addAccount = async (newAccount: UserResponse) => {
    setSuccessMessage("ユーザーを登録しました");
    await fetchData();
  };

  const handleArchivedModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsArchivedMode(event.target.checked);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
  };

  const getArchiveIcon = () => {
    return isArchivedMode ? <CheckBox /> : <CheckBoxOutlineBlank />;
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case "admin": return "管理者";
      case "internal_worker": return "社内作業者";
      case "external_worker": return "委託先作業者";
      default: return role;
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case "admin": return "success";
      case "internal_worker": return "warning";
      case "external_worker": return "primary";
      default: return "default";
    }
  };

  const getStatusLabel = (status: AccountStatus) => {
    switch (status) {
      case "active": return "有効";
      case "inactive": return "無効";
      default: return status;
    }
  };

  const getStatusColor = (status: AccountStatus) => {
    switch (status) {
      case "active": return "success";
      case "inactive": return "default";
      default: return "default";
    }
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "ユーザー名",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "login_id",
      headerName: "ログインID",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "role",
      headerName: "役割",
      minWidth: 120,
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={getRoleLabel(params.value as UserRole)}
          color={getRoleColor(params.value as UserRole)}
        />
      ),
    },
    {
      field: "status",
      headerName: "ステータス",
      minWidth: 120,
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.value as AccountStatus)}
          color={getStatusColor(params.value as AccountStatus)}
        />
      ),
    },
    {
      field: "updated_at",
      headerName: isArchivedMode ? "削除日" : "更新日",
      minWidth: 120,
      flex: 1,
      renderCell: (params) => {
        const dateValue = isArchivedMode ? params.row.deleted_at : params.value;
        return dayjs(dateValue).format("YYYY/M/D");
      },
    },
    {
      field: "action",
      headerName: "",
      renderCell: (params: GridRenderCellParams) => (
        <ActionMenu
          params={params}
          isArchived={isArchivedMode}
          onDelete={deleteAccount}
          onRestore={restoreAccount}
          onError={setErrorMessage}
        />
      ),
      width: 60,
      sortable: false,
    },
  ];

  return (
    <PageContainer>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
          アカウント管理
        </Typography>
        <Box display="flex" justifyContent="flex-end" alignItems="center">
          {!isArchivedMode && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenRegisterDialog(true)}
            >
              アカウント登録
            </Button>
          )}
        </Box>
      </Box>

      {/* アーカイブボタンエリア */}
      <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
        <Button
          startIcon={getArchiveIcon()}
          onClick={() => handleArchivedModeChange({ target: { checked: !isArchivedMode } } as React.ChangeEvent<HTMLInputElement>)}
          variant="outlined"
          size="small"
        >
          アーカイブ
        </Button>
      </Box>

      <AccountCreateDialog
        openRegisterDialog={openRegisterDialog}
        setOpenRegisterDialog={setOpenRegisterDialog}
        addAccount={addAccount}
        onSuccess={setSuccessMessage}
      />

      <DataGrid
        loading={isLoading}
        onRowDoubleClick={(params) => {
          navigate(PATHS.ACCOUNT_DETAIL.replaceAll(":id", params.row.id.toString()));
        }}
        rows={data}
        columns={columns}
        rowSelection={false}
        isRowSelectable={() => false}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
        disableColumnSelector
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        hideFooterSelectedRowCount
        disableDensitySelector
        pageSizeOptions={[5, 10, 20, 25]}
        paginationMode="server"
        rowCount={total}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
      />

      {/* 成功メッセージ */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>

      {/* エラーメッセージ */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default AccountList;